<template>
  <v-container fluid class="pa-4">
    <div class="d-flex align-center mb-1">
      <h2 class="text-h5 font-weight-bold">Autodiagnóstico SAC</h2>
      <v-spacer />
       <v-btn text :to="publicLeaderboardPath" target="_blank" rel="noopener">
        <v-icon left>mdi-trophy-outline</v-icon> Ver ranking público
      </v-btn>
    </div>
    <p class="grey--text mb-4">Evalúa tu situación actual respecto al estándar SOFOM Alta Calidad.</p>

    <v-card v-if="loading" class="pa-8 text-center">
      <v-progress-circular indeterminate />
    </v-card>
    <div v-else>
      <v-alert v-if="inProgress" type="info" class="mb-4" prominent>
        Tienes un intento en curso. <v-btn class="ml-3" color="primary" :to="`/sac/responses/${inProgress._id}`">Continuar</v-btn>
      </v-alert>

      <v-card class="mb-4 pa-4">
        <div class="d-flex align-center">
          <div>
            <h3 class="text-h6">Iniciar nuevo intento</h3>
            <p class="text-caption grey--text mb-0">Duración estimada: 15-20 minutos. Puedes pausar y reanudar.</p>
          </div>
          <v-spacer />
          <v-btn color="primary" :loading="starting" @click="startNew" :disabled="!!inProgress">
            <v-icon left>mdi-play</v-icon> Iniciar
          </v-btn>
        </div>
      </v-card>

      <h3 class="text-h6 mb-2">Historial</h3>
      <v-card>
        <v-list v-if="completedHistory.length">
          <v-list-item
            v-for="r in completedHistory"
            :key="r._id"
            :to="`/sac/responses/${r._id}/result`"
            link
          >
            <v-list-item-avatar>
              <v-icon :color="colorForLevel(r.overallLevel)">mdi-chart-line</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>
                {{ r.overallLevel || 'En curso' }}
                <span v-if="r.totalScore != null" class="ml-2 font-weight-bold">{{ Math.round(r.totalScore) }}/100</span>
              </v-list-item-title>
              <v-list-item-subtitle>{{ formatDate(r.completedAt || r.startedAt) }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <div class="d-flex align-center publish-toggle">
                <span class="mr-2 text-body-2">Mostrar en ranking</span>
                <v-tooltip location="top" :disabled="togglingId === r._id">
                  <template #activator="{ on, attrs }">
                    <v-switch
                      v-bind="attrs"
                      v-on="on"
                      :loading="togglingId === r._id"
                      :input-value="!!r.publishedOnLeaderboard"
                      color="primary"
                      hide-details
                      class="mt-0 pt-0"
                      @click.stop.prevent="togglePublish(r)"
                    />
                  </template>
                  <span v-if="r.publishedOnLeaderboard">
                    Tu resultado aparecerá en la página pública de mejores resultados.
                  </span>
                  <span v-else>
                    Activa para mostrar este intento en la página pública de mejores resultados.
                  </span>
                </v-tooltip>
              </div>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-card-text v-else class="grey--text text-center">Sin intentos completados aún.</v-card-text>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import { mapMutations } from 'vuex';
import {
  getMyResponses,
  getMyInProgress,
  createResponse,
  setPublishOnLeaderboard,
} from '../../api/sac';

export default {
    name: 'SacMemberStart',
    data() {
        return {
            loading: true,
            starting: false,
            inProgress: null,
            history: [],
            togglingId: null,
        };
    },
    computed: {
        publicLeaderboardPath() {
            return '/sac/leaderboard';
        },
        completedHistory() {
            return (this.history || []).filter((r) => r.status === 'COMPLETED');
        },
    },
    async mounted() {
        await this.load();
    },
    methods: {
        ...mapMutations('notification', ['show']),
        colorForLevel(level) {
            return { Crítico: 'red', Básico: 'orange', Aceptable: 'amber', Avanzado: 'green', Excelente: 'blue' }[level] || 'grey';
        },
        formatDate(d) {
            return d ? new Date(d).toLocaleString('es-MX') : '';
        },
        async load() {
            this.loading = true;
            try {
                const [inProg, mine] = await Promise.all([getMyInProgress(), getMyResponses({ limit: 20 })]);
                this.inProgress = inProg.response;
                this.history = mine.responses || [];
            } catch (e) {
                this.show({
                    text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo cargar el autodiagnóstico',
                    color: 'error',
                });
            } finally {
                this.loading = false;
            }
        },
        async startNew() {
            this.starting = true;
            try {
                const r = await createResponse();
                this.$router.push(`/sac/responses/${r.response._id}`);
            } catch (e) {
                this.show({
                    text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo iniciar el autodiagnóstico',
                    color: 'error',
                });
            } finally {
                this.starting = false;
            }
        },
        async togglePublish(r) {
            this.togglingId = r._id;
            try {
                const out = await setPublishOnLeaderboard(r._id, !r.publishedOnLeaderboard);
                r.publishedOnLeaderboard = !!out.publishedOnLeaderboard;
                r.publishedOnLeaderboardAt = out.response?.publishedOnLeaderboardAt || null;
                this.show({
                    text: r.publishedOnLeaderboard
                        ? 'Tu resultado ahora aparece en el ranking público'
                        : 'Tu resultado ya no aparece en el ranking público',
                    color: 'success',
                });
            } catch (e) {
                this.show({
                    text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo actualizar',
                    color: 'error',
                });
            } finally {
                this.togglingId = null;
            }
        },
    },
};
</script>
